import { type FC, useMemo } from "react";
import classNames from "classnames";
import { CtaPrimary } from "../..";
import { NftImages } from "./NftImages";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useRouter } from "next/navigation";
import { generalLiterals } from "../../../literals";
import { BasicProps } from "components/types/BasicProps";
import { INft } from "logic/types/nft/INft";
import Link from "next/link";
import track from "logic/track";
import { useFaucetEnabled } from "logic/hooks";

type Props = BasicProps & {
  onDepositClick: () => void;
  onBorrowClick: () => void;
  nfts: INft[];
  isLoading: boolean;
};

export const MySupportedNfts: FC<Props> = (props) => {
  const { className, onBorrowClick, nfts, isLoading } = props;
  const router = useRouter();
  const isFaucetEnabled = useFaucetEnabled();

  const primaryButtonLiteral = useMemo(() => {
    if (nfts) {
      return !nfts.length && isFaucetEnabled
        ? generalLiterals.myAvailableNfts.faucet
        : generalLiterals.myAvailableNfts.borrowCta;
    }
  }, [nfts]);

  const isCtaPrimaryDisabled = useMemo<boolean>(
    () => isFaucetEnabled === false && nfts.length === 0,
    [nfts.length]
  );

  const handlePrimaryButton = () => {
    if (nfts!.length) {
      track.click("borrow_click_createloan");

      onBorrowClick();
    } else if (isFaucetEnabled) {
      handleDepositFromWallet();
    }
  };

  const handleDepositFromWallet = () => router.push(`/faucet`);

  return (
    <>
      <section
        className={classNames(
          "max-w-7xl max-h-[588px] h-full mx-auto mb-11 p-0.5 border-unlockd  rounded-xl",
          [className]
        )}
      >
        <div className="w-full h-full pt-8 px-8 text-white bg-secondary rounded-xl flex flex-col items-center">
          <h1 className="font-bold text-2xl mb-2.5">
            {generalLiterals.myAvailableNfts.title}
          </h1>
          <h2 className="mb-9">{generalLiterals.myAvailableNfts.subheader}</h2>

          {!isLoading && nfts?.length === 0 && (
            <div className="w-full h-48 flex flex-col justify-center items-center gap-4">
              <p className="text-center">
                You don't have any supported NFT.
                <br />
                Check the list of supported assets{" "}
                <Link
                  className="text-primary font-bold"
                  target="_blank"
                  href="https://docs.unlockd.finance/unlockd-at-a-glance/supported-assets"
                >
                  here
                </Link>
                .
              </p>
            </div>
          )}

          {!isLoading && nfts!.length !== 0 && (
            <NftImages className="h-40 mb-9 mr-0" nfts={nfts!} />
          )}

          {isLoading && (
            <div className="mb-9 w-full h-40 flex justify-center items-center">
              <AiOutlineLoading3Quarters className="animate-spin w-10 h-10" />
            </div>
          )}

          <div className="flex flex-col gap-4 w-max mx-auto pb-8">
            {!isLoading && (
              <>
                <CtaPrimary
                  className="w-60"
                  onClick={handlePrimaryButton}
                  disabled={isCtaPrimaryDisabled}
                >
                  {primaryButtonLiteral}
                </CtaPrimary>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
